'use client';

/**
 * ErrorToastHost.tsx
 *
 * Subscriber for the error-toast bus. Mounted once in app/layout.
 * Renders a stack of dismissable bottom-right toasts when the api.ts
 * fetch wrapper sees a non-2xx response.
 *
 * Each toast shows the CRSC-XXXX code and a Copy button that puts
 * `code | error_id | request_id | url` on the clipboard so Chris can
 * paste straight into a debug session and `git blame` the failing line.
 *
 * Auto-dismiss after 8s. Manually dismissable via the X. Up to 3 toasts
 * stacked at once; older ones are evicted to keep the screen calm.
 */

import { useEffect, useState } from 'react';
import { subscribeErrorToast, type BackendErrorEnvelope } from '@/lib/error-toast';

interface ToastEntry extends BackendErrorEnvelope {
  id: string;
  shownAt: number;
}

const MAX_TOASTS = 3;
const AUTO_DISMISS_MS = 8000;

export default function ErrorToastHost() {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  useEffect(() => {
    const unsubscribe = subscribeErrorToast((e) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => {
        const next = [...prev, { ...e, id, shownAt: Date.now() }];
        if (next.length > MAX_TOASTS) next.splice(0, next.length - MAX_TOASTS);
        return next;
      });
    });
    return unsubscribe;
  }, []);

  // Auto-dismiss after AUTO_DISMISS_MS. One interval handles all entries.
  useEffect(() => {
    if (toasts.length === 0) return;
    const interval = setInterval(() => {
      setToasts((prev) => prev.filter((t) => Date.now() - t.shownAt < AUTO_DISMISS_MS));
    }, 1000);
    return () => clearInterval(interval);
  }, [toasts.length]);

  function dismiss(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  if (toasts.length === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2 sm:max-w-sm"
    >
      {toasts.map((t) => (
        <ErrorToastCard key={t.id} entry={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ErrorToastCard({ entry, onDismiss }: { entry: ToastEntry; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const copyValue = [
    entry.code ? `Code: ${entry.code}` : null,
    entry.error_id ? `Error ID: ${entry.error_id}` : null,
    entry.request_id ? `Request ID: ${entry.request_id}` : null,
    entry.url ? `URL: ${entry.url}` : null,
    entry.status ? `Status: ${entry.status}` : null,
  ].filter(Boolean).join(' | ');

  async function copy() {
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // navigator.clipboard unavailable; treat as no-op.
    }
  }

  // The display message is intentionally short. The detail goes on the
  // clipboard via Copy. We don't want a 4-line stack trace covering
  // half the screen.
  const heading = entry.message_for_operator
    || (entry.status && entry.status >= 500 ? 'Something went wrong on our side.' : 'Request failed.');

  return (
    <div className="pointer-events-auto rounded-xl border border-red-200 bg-white p-3 shadow-lg dark:border-red-900 dark:bg-[#141414]">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-red-700 dark:text-red-400">{heading}</div>
          {entry.code && (
            <div className="mt-0.5 font-mono text-[11px] text-gray-600 dark:text-gray-400">
              Code: <span className="font-semibold">{entry.code}</span>
            </div>
          )}
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      {(entry.code || entry.error_id || entry.request_id) && (
        <div className="mt-2">
          <button
            onClick={copy}
            className="rounded-md border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {copied ? 'Copied' : 'Copy details'}
          </button>
        </div>
      )}
    </div>
  );
}
